import type { Staff } from "@/lib/authv2/store";
import { ACTION } from "@/lib/auth/actions";
import { ROLE } from "@/lib/auth/roles";
import { hasPermission } from "@/lib/authv2/permissions";
import { opsRole } from "@/lib/auth/page-access";

// Bốn quyền tách rời, và ranh giữa chúng CHÍNH LÀ ràng buộc "kín thông tin"
// của CEO (spec 8.1b). Đừng gộp cho tiện:
//
//   xem hồ sơ MÌNH        — mọi người (cửa chỉ trả hồ sơ người gọi)
//   xem tiêu chí NGHỀ MÌNH — mọi người (máy chủ lọc theo nghề)
//   xem bảng CẢ ĐỘI       — quản lý, và backend cắt theo chi nhánh
//   xem TRỌN BỘ tiêu chí  — chỉ admin/giám đốc (tài sản vận hành)
//
// Phạm vi chi nhánh do backend cắt (auth.BranchScope), không phải màn hình
// lọc — "xem được trang" không có nghĩa "xem được người mọi cơ sở".

/**
 * Xem thông tin thật của bài đang chấm: khách, ê-kíp, sale (CEO chốt 31/10/2026).
 * Gương của `canSeeBlindInfo` ở máy chủ. Hỏi vai thật, không qua danh phận.
 */
export function canSeeBlindInfo(staff: Staff | null): boolean {
  return staff?.role === ROLE.Admin || staff?.role === ROLE.Director;
}

/** Xem hồ sơ hiệu suất của chính mình. */
export function canViewMyPerformance(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceReadSelf);
}

/**
 * Mở được TRANG Hiệu suất — rộng hơn `canViewMyPerformance` một cách CỐ Ý.
 *
 * Cửa "Của tôi" chứa thẻ CÔNG KHAI theo lệnh CEO (spec 4.2): "Sổ quỹ chung",
 * `show: true` vô điều kiện trong PerformancePage (thẻ "Vinh danh" đã về trang
 * Xếp hạng 01/09/2026, nhưng cổng vẫn hỏi `leaderboard.read` vì quyền đó là
 * dấu hiệu người này có vai hợp lệ). Nếu cổng chỉ hỏi `performance.read.self`
 * thì người thiếu đúng quyền đó
 * — thường vì Casdoor chưa gán vai, không phải vì bị cấm — mất luôn cả hai thẻ
 * công khai, dù máy chủ vẫn sẵn sàng trả dữ liệu cho họ.
 *
 * Nên hỏi cả ba quyền: có BẤT KỲ quyền nào thì trang cũng còn thứ để xem. Từng
 * thẻ bên trong vẫn tự gác, và máy chủ gác lại lần nữa.
 */
export function canOpenPerformancePage(staff: Staff | null): boolean {
  if (!staff) return false;
  return (
    hasPermission(ACTION.PerformanceReadSelf) ||
    hasPermission(ACTION.PenaltyFundRead) ||
    hasPermission(ACTION.PerformanceLeaderboardRead)
  );
}

/** Xem bảng điểm cả đội (backend cắt theo chi nhánh). */
export function canViewTeamPerformance(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceReadTeam);
}

/** Chấm phiếu rubric — QC và cấp quản lý. Nghề nào thì máy chủ gác tiếp. */
export function canReviewPerformance(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceReviewWrite);
}

/** Ba khu chấm vào người của phiếu kiểm cơ sở. */
export type SiteAreaKey = "counter" | "photo" | "makeup";
export type SiteCheckAccess = { areas: readonly SiteAreaKey[]; facility: boolean };

const SITE_ALL: SiteCheckAccess = { areas: ["counter", "photo", "makeup"], facility: true };

/**
 * Người này chấm được khu nào ở thẻ "Kiểm cơ sở" (QĐ 0503, CEO chốt 1a · 2a,
 * 31/10/2026). Gương của `siteAccessFor` ở `performance/sitecheck.go` — máy chủ
 * gác thật, bản này để khỏi vẽ khu bấm vào là bị từ chối.
 *
 *   QC cơ sở (phòng lễ tân) — cả bốn khu
 *   QC chụp ảnh             — khu chụp
 *   QC trang điểm           — khu trang điểm
 *   QC sửa hình, QC CSKH    — không khu nào, không có thẻ
 *   giám đốc, quản lý cụm   — cả bốn khu
 *
 * Danh sách phòng giám đốc gán (migration 0331) thắng ô phòng, và được cộng khu.
 */
export function siteCheckAccess(staff: Staff | null): SiteCheckAccess {
  if ((staff?.role ?? "").toLowerCase().trim() !== ROLE.QC) return SITE_ALL;
  const gan = (staff?.qcDepartments ?? []).filter((d) => d.trim() !== "");
  const depts = (gan.length > 0 ? gan : [staff?.department ?? ""]).map((d) =>
    d.toLowerCase().trim(),
  );
  if (depts.includes("reception")) return SITE_ALL;
  const areas: SiteAreaKey[] = [];
  if (depts.some((d) => d.startsWith("photo"))) areas.push("photo");
  if (depts.includes("makeup")) areas.push("makeup");
  return { areas, facility: false };
}

/** Thẻ "Kiểm cơ sở": có quyền chấm phiếu và còn ít nhất một khu để kiểm. */
export function canCheckSite(staff: Staff | null): boolean {
  if (!canReviewPerformance(staff)) return false;
  const a = siteCheckAccess(staff);
  return a.facility || a.areas.length > 0;
}

/**
 * Thẻ "Chấm bài" — rút bài theo buổi từ hàng đợi mù.
 *
 * QC chỉ rút được bài của nghề có hàng đợi (ảnh, trang điểm, sửa hình — khớp
 * `queueableKinds` ở qcqueue.go). QC phòng khác (lễ tân, CSKH, chưa gán phòng)
 * mở thẻ này chỉ gặp hàng đợi rỗng, nên ẩn đi để bàn mở thẳng vào "Kiểm cơ sở"
 * (CEO chốt 2a, 28/09/2026). Cấp quản lý chấm mọi nghề nên giữ nguyên.
 */
export function canDrawRubricSessions(staff: Staff | null): boolean {
  if (!canReviewPerformance(staff) || !staff) return false;
  if ((staff.role ?? "").toLowerCase().trim() !== ROLE.QC) return true;
  const dept = (staff.department ?? "").toLowerCase().trim();
  return dept.startsWith("photo") || dept === "makeup" || dept === "retouch";
}

/**
 * Chấm phiếu TUẦN — TRƯỞNG PHÒNG CSKH / Sale.
 *
 * Dùng chung quyền với phiếu QC vì máy chủ gác tiếp theo VAI + PHÒNG BAN (một
 * quyền, hai đường vào). Thẻ chỉ hiện cho người thật sự chấm được: bấm vào mà
 * bị máy chủ đuổi thì trông y như hỏng.
 *
 * Điều kiện VAI thêm 02/09/2026. Thiếu nó, QC phòng CSKH (chị Dương) thấy thẻ
 * "Phiếu tuần" ngay trên màn của mình, bấm vào, tick xong, bấm gửi và ăn 403
 * "phiếu tuần chỉ trưởng phòng hoặc cấp quản lý mới chấm được" — công tick
 * mất trắng, mà chị ấy không có cách nào biết trước.
 *
 * Điều kiện PHÒNG chỉ áp cho trưởng phòng: cấp quản lý (admin / giám đốc /
 * quản lý cụm) chấm được phiếu tuần của mọi phòng, đúng như máy chủ
 * (`rubric.go`, nhánh `weeklyDepts`).
 */
/** Hai phòng dùng phiếu tuần (CEO chốt 29/08/2026): CSKH và Sale. */
export const WEEKLY_DEPARTMENTS = ["sale", "cskh"] as const;
export function isWeeklyDepartment(dept: string | null | undefined): boolean {
  return (WEEKLY_DEPARTMENTS as readonly string[]).includes(dept ?? "");
}

export function canReviewWeekly(staff: Staff | null): boolean {
  if (!staff) return false;
  if (!hasPermission(ACTION.PerformanceReviewWrite)) return false;
  // QC chấm phiếu tuần mọi phòng (04/10/2026), y máy chủ (`rubric.go`).
  if (canPickWeeklyDepartment(staff)) return true;
  const role = (staff.role ?? "").toLowerCase();
  if (role !== ROLE.Manager) return false;
  return isWeeklyDepartment(staff.department);
}

/**
 * Được CHỌN phòng trên màn phiếu tuần — vai đứng trên phòng ban và QC (chấm
 * mọi phòng từ 04/10/2026). Trưởng
 * phòng chấm phòng mình, không có gì để chọn (kiểm toán 05/09/2026, CC-01).
 */
export function canPickWeeklyDepartment(staff: Staff | null): boolean {
  if (!staff) return false;
  const role = (staff.role ?? "").toLowerCase();
  return (
    role === ROLE.Admin ||
    role === ROLE.Director ||
    role === ROLE.AreaManager ||
    role === ROLE.QC
  );
}

/**
 * Chấm phiếu KÉO KHÁCH (chủ studio chốt 07/09/2026).
 *
 * Trưởng cửa hàng chấm nhân sự cơ sở mình; quản lý cụm / giám đốc / quản trị
 * chấm được cả trưởng cửa hàng. Hỏi ĐÚNG quyền `performance.growth.write`,
 * không mượn quyền phiếu tuần — hai phiếu hai danh sách vai khác nhau.
 *
 * Máy chủ vẫn gác lại lần nữa (`canGradeGrowth` trong growth.go): thẻ ẩn
 * KHÔNG phải hàng rào.
 */
export function canGradeGrowth(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceGrowthWrite);
}

/**
 * Chấm phiếu đồng phục (QĐ 0244 · 3b): trưởng cửa hàng, QC, quản lý cụm, giám
 * đốc — mỗi người giữ một phiếu. QC chấm mọi phòng (04/10/2026); máy chủ gác
 * lại theo phạm vi cơ sở.
 */
export function canGradeUniform(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceUniformWrite);
}

/** Tặng hoa ghi nhận. */
export function canGiveKudos(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceKudosWrite);
}

/**
 * Ghi lời khen của KHÁCH (thư khen, tip, khách 5⭐) — QĐ 0338. Bản sao luật
 * `canEnterCustomerKudos` ở máy chủ (`kudos_duyet.go`): người nhận thư, tin
 * nhắn, tip của khách. Người khác vẫn tặng "Đồng nghiệp khen" như cũ.
 */
export function canEnterCustomerKudos(staff: Staff | null): boolean {
  if (!staff) return false;
  // Trưởng ca như Trưởng cửa hàng (QĐ 0425, CEO 1b) — gương `auth.OpsRole`.
  const role = opsRole(staff).toLowerCase();
  if (
    role === ROLE.Admin ||
    role === ROLE.Director ||
    role === ROLE.AreaManager ||
    role === ROLE.BranchManager ||
    role === ROLE.QC
  ) {
    return true;
  }
  return (staff.department ?? "").trim().toLowerCase() === "cskh";
}

/** Lý do in trên các lựa chọn nguồn khách bị khoá. Cùng ý câu máy chủ trả 403. */
export const CUSTOMER_KUDOS_LOCK_REASON =
  "Chỉ CSKH, QC và cấp quản lý ghi lời khen của khách";

/**
 * Duyệt lời khen của khách — quản trị, giám đốc, quản lý cụm (QĐ 0338). Đi chung
 * quyền duyệt bằng chứng, nhưng trưởng cửa hàng có quyền đó (duyệt review Google
 * tiệm mình) mà KHÔNG duyệt lời khen — họ là người ghi chính.
 */
export function canDecideKudos(staff: Staff | null): boolean {
  if (!staff || !hasPermission(ACTION.PerformanceEvidenceApprove)) return false;
  const role = (staff.role ?? "").toLowerCase();
  return role === ROLE.Admin || role === ROLE.Director || role === ROLE.AreaManager;
}

/** Sửa cấu hình khung — admin/giám đốc. Cũng là quyền xem TRỌN BỘ tiêu chí. */
export function canEditFramework(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceConfigWrite);
}

/** Sửa ảnh mẫu theo bậc của tiêu chí chấm QC (chấm QC bản 3) — quản trị, giám đốc. */
export function canManageRubricExamples(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceRubricExamplesManage);
}

// ─── Đợt 3–7 ───────────────────────────────────────────────────────────────
//
// Cùng nếp với bốn quyền trên: mỗi màn hỏi ĐÚNG quyền của việc nó làm, không
// gộp thành "quyền quản lý" chung. Gộp là sớm muộn có người thấy được thứ họ
// không nên thấy chỉ vì họ cần làm một việc khác.

/** Chấm đồng đội cùng ca. */
export function canPeerReview(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformancePeerWrite);
}

/** Ghi lượt khách vãng lai — lễ tân. */
export function canLogWalkin(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceWalkinWrite);
}

/** Nộp bằng chứng (ảnh trước–sau, đánh giá Google). */
export function canSubmitEvidence(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceEvidenceWrite);
}

/**
 * Duyệt bằng chứng do người khác nộp — hàng chờ duyệt review Google (CEO chốt
 * 3a, QĐ 0322). Máy chủ cắt tiếp theo cơ sở: trưởng cửa hàng chỉ cơ sở mình.
 */
export function canApproveEvidence(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceEvidenceApprove);
}

/** Chốt kỳ: khoá sổ, điều chỉnh điểm sau khoá. */
export function canManageCycle(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceCycleWrite);
}

/**
 * Nút "Khoá sổ" tay (rà soát 15/10/2026, N10 — CEO chốt Câu 5): chỉ giám đốc và
 * quản trị. Quản lý cụm vẫn giữ quyền chốt kỳ cho phần còn lại của màn. Máy chủ
 * chặn lại ở `LockMonthFor`.
 */
export function canLockMonth(staff: Staff | null): boolean {
  if (!staff || !canManageCycle(staff)) return false;
  return staff.role === ROLE.Admin || staff.role === ROLE.Director;
}

/** Xét kháng nghị. */
export function canDecideAppeal(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceAppealDecide);
}

/** Ký nhận quy chế — mọi người, và là ĐIỀU KIỆN BẬT khung (Điều 118 BLLĐ). */
export function canAckPolicy(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformancePolicyAck);
}

/** Đặt mốc chỉ tiêu tháng. */
export function canSetTarget(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceTargetWrite);
}

/**
 * Xem sổ tháng đỏ của NGƯỜI KHÁC — admin, giám đốc, quản lý cụm, y máy chủ
 * (performance/me.go `isFullView`). Quyền thô `performance.redmonth.read` được
 * cấp cho MỌI vai để ai cũng đọc được sổ của chính mình, nên hỏi mỗi quyền thô
 * là mở "Điều hành khung" cho cả xưởng (đo thật 13/09/2026 bằng vé thợ — hàng
 * 8 màn CEO duyệt). Vai `manager` / trưởng cửa hàng / QC có quyền thô nhưng máy
 * chủ vẫn chỉ trả sổ của họ, nên cũng không mở cửa này.
 */
export function canReadRedMonths(staff: Staff | null): boolean {
  if (!staff) return false;
  if (!hasPermission(ACTION.PerformanceRedMonthRead)) return false;
  const role = (staff.role ?? "").toLowerCase();
  return isTopBrass(role) || role === ROLE.AreaManager;
}

/**
 * Khối "Lỗi lọt lưới QC" trên màn Giám sát — admin, giám đốc, quản lý cụm, y máy
 * chủ (performance/dot4.go NetGapSuggestions → `isFullView`). QC có quyền
 * performance.fraud.read nên trước đây thấy khối này, máy chủ từ chối và màn
 * hiện chữ xanh «Không có lỗ nào» (kiểm thử đóng vai 17/10/2026, L2-07).
 */
export function canReadNetGaps(staff: Staff | null): boolean {
  if (!staff) return false;
  const role = (staff.role ?? "").toLowerCase();
  return isTopBrass(role) || role === ROLE.AreaManager;
}

/**
 * Xem sổ tháng đỏ của CHÍNH MÌNH — hộp "Tháng đỏ của tôi" trên màn Hiệu suất
 * của tôi (QĐ 0132 nhóm B câu 1, CEO chốt 1a ngày 13/09/2026). Chỉ hỏi quyền
 * thô: máy chủ cấp nó cho mọi vai và tự thu về sổ của người gọi khi không
 * truyền mã. Tách khỏi `canReadRedMonths` (của người khác) để cửa "Điều hành
 * khung" không mở lại cho cả xưởng.
 */
export function canReadOwnRedMonths(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceRedMonthRead);
}

// ─── 7a: ai ghi được hai sổ (quyết định 0132 §7, CEO chốt 12/09/2026) ───────
//
// Quyền thô `performance.marketing.write` mở cho cả `staff` lẫn `manager` vì
// Casdoor không biết phòng ban — tức thợ chụp, trang điểm, sửa ảnh, lễ tân đều
// CÓ nó. Máy chủ gác tiếp theo phòng/vai (performance/dot5.go:
// canWriteMarketingLog / canLogClusterMeeting) và trả 403. Hai hàm dưới hỏi
// ĐÚNG câu máy chủ hỏi: không thì menu "Điều hành khung" hiện với cả xưởng và
// dẫn tới một trang chỉ có hai ô mà bấm gửi là bị từ chối.

const MARKETING_DEPT = "marketing";

function isTopBrass(role: string): boolean {
  return role === ROLE.Admin || role === ROLE.Director;
}

/** Ghi đầu việc vào sổ marketing — phòng marketing (mọi vai), admin, giám đốc. */
export function canLogMarketing(staff: Staff | null): boolean {
  if (!staff) return false;
  if (!hasPermission(ACTION.PerformanceMarketingWrite)) return false;
  const role = (staff.role ?? "").toLowerCase();
  if (isTopBrass(role)) return true;
  return (staff.department ?? "").trim().toLowerCase() === MARKETING_DEPT;
}

/**
 * Ghi biên bản họp cụm — trưởng cửa hàng và quản lý cụm, nhận theo VAI hoặc
 * theo PHÒNG (hồ sơ cũ ghi chức danh vào cột phòng ban), cùng admin/giám đốc.
 */
export function canLogClusterMeeting(staff: Staff | null): boolean {
  if (!staff) return false;
  if (!hasPermission(ACTION.PerformanceMarketingWrite)) return false;
  const role = (staff.role ?? "").toLowerCase();
  if (isTopBrass(role)) return true;
  const dept = (staff.department ?? "").trim().toLowerCase();
  return [role, dept].some((v) => v === ROLE.BranchManager || v === ROLE.AreaManager);
}

/** Xem dấu hiệu bất thường + lỗi lọt lưới QC. */
export function canReadFraudSignals(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceFraudRead);
}

/** Trả về đào tạo / chứng nhận lại tay nghề. */
export function canManageTraining(staff: Staff | null): boolean {
  if (!staff) return false;
  return hasPermission(ACTION.PerformanceTrainingWrite);
}

// ─── Ba CỬA của khung hiệu suất (CEO chốt 01/09/2026) ───────────────────────
//
// Trước 01/09 cả 12 thẻ dồn vào một trang: người bị chấm và người đi chấm đi
// chung một cửa, còn việc chấm thì nằm ngoài dòng công việc nên người bận bỏ
// qua — mà họ không chấm thì nhân viên MẤT ĐIỂM OAN. Nay tách ba cửa theo
// việc, mỗi cửa một mục menu:
//
//   Hiệu suất của tôi — điểm, tiền, phạt, chuỗi, và các ô tự nhập của mình
//   Chấm điểm         — QC chấm phiếu, kiểm cơ sở, trưởng phòng tick phiếu tuần
//   Điều hành khung   — bảng đội, chốt kỳ, mốc, giám sát, cấu hình
//
// Hai hàm dưới là NGUỒN CHÂN LÝ dùng chung cho cả menu (nav-content-gates) lẫn
// trang. Một chỗ viết, hai chỗ gọi — tách ra là sớm muộn menu hiện một đằng,
// trang chặn một nẻo.

/** Mở được cửa "Chấm điểm" — QC chấm phiếu, hoặc trưởng phòng tick phiếu tuần. */
export function canOpenReviewDesk(staff: Staff | null): boolean {
  // Trưởng cửa hàng KHÔNG có quyền chấm phiếu QC, nên thiếu vế thứ ba là họ
  // không thấy cửa dẫn tới phiếu kéo khách của chính cơ sở mình.
  return (
    canReviewPerformance(staff) ||
    canReviewWeekly(staff) ||
    canGradeGrowth(staff) ||
    canGradeUniform(staff) ||
    canApproveEvidence(staff)
  );
}

/**
 * Mở được cửa "Điều hành khung".
 *
 * Liệt kê ĐỦ mọi quyền dẫn tới một thẻ bên trong, không rút gọn thành "quyền
 * quản lý": người chỉ có quyền xét kháng nghị vẫn phải vào được cửa này, còn
 * người không có quyền nào thì không nên thấy một mục menu dẫn tới trang trống.
 */
export function canOpenFrameworkDesk(staff: Staff | null): boolean {
  return (
    canViewTeamPerformance(staff) ||
    canManageCycle(staff) ||
    canDecideAppeal(staff) ||
    canManageTraining(staff) ||
    canSetTarget(staff) ||
    canReadRedMonths(staff) ||
    canLogMarketing(staff) ||
    canLogClusterMeeting(staff) ||
    canReadFraudSignals(staff) ||
    canEditFramework(staff)
  );
}

/**
 * Duyệt thưởng KPI tháng (QĐ 0427) — CHỈ Tổng giám đốc (`admin`), không phải
 * giám đốc. CEO chốt 20/10/2026: mức báo tổng thưởng là việc riêng của Tổng
 * giám đốc, không ai khác được thấy. Máy chủ gác lại lần nữa (`requireCEO`).
 */
export function canApproveFundCap(staff: Staff | null): boolean {
  if (!staff) return false;
  return (staff.role ?? "").toLowerCase().trim() === ROLE.Admin;
}
